import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  TextField,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import {
  LocationOn,
  AttachMoney,
  Timer,
  VerifiedUser,
  Search,
  MyLocation,
  HomeWork,
  Assignment,
  Bolt
} from '@mui/icons-material';
import simpleVerificationService from '../services/simpleVerificationService';
import useProfileGate from '../hooks/useProfileGate';
import api from '../services/api';
import { brand } from '../theme/colors';
import VerifierMarketplacePage from './VerifierMarketplacePage';
import VerifierIdentityBanner from '../components/verifiers/VerifierIdentityBanner';

/**
 * Find Work — unified verifier feed.
 *
 * One list merging every kind of claimable verification work, all fixed-fee
 * Accept & Start (quoting retired):
 *  - quick jobs        (simple_verifications, fee set at creation)
 *  - Property Checks   (offers pushed to this verifier + paid checks broadened
 *                       to everyone after 24h unclaimed)
 *  - verification jobs (owner project/milestone requests at the platform's
 *                       fixed $75 fee)
 */

const TYPE_META = {
  quick: { label: 'Quick job', color: '#0d9488', icon: <Bolt sx={{ fontSize: 16 }} /> },
  property_check_offer: { label: 'Property Check — reserved for you', color: '#7c3aed', icon: <HomeWork sx={{ fontSize: 16 }} /> },
  property_check_open: { label: 'Property Check — paid & ready', color: '#7c3aed', icon: <HomeWork sx={{ fontSize: 16 }} /> },
  verification_job: { label: 'Project verification', color: '#2563eb', icon: <Assignment sx={{ fontSize: 16 }} /> }
};

const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const coordsOf = (obj) => {
  if (!obj) return null;
  const lat = Number(obj.lat ?? obj.latitude);
  const lng = Number(obj.lng ?? obj.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const AvailableSimpleVerificationsPage = () => {
  const navigate = useNavigate();
  const { profileComplete } = useProfileGate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsDenied, setGpsDenied] = useState(false);
  const [manualCity, setManualCity] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [bannerKey, setBannerKey] = useState(0);
  const [acceptingKey, setAcceptingKey] = useState(null);

  useEffect(() => {
    captureGPS();
    // Track that verifier has browsed gigs (for Getting Started checklist)
    localStorage.setItem('eqaya_verifier_browsed_gigs', 'true');
    // Also persist server-side so it survives device switches
    api.put('/auth/users/profile', { browsed_verifications: true }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Work is loadable without GPS — distance chips just stay hidden.
    loadWork(gpsLocation, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsLocation]);

  const captureGPS = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsDenied(false);
          setGpsLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (gpsErr) => {
          console.error('GPS error:', gpsErr);
          setGpsDenied(true);
        }
      );
    } else {
      setGpsDenied(true);
    }
  };

  const loadWork = useCallback(async (gps, city) => {
    setLoading(true);
    setError(null);
    try {
      const quickParams = gps
        ? { lat: gps.lat, lng: gps.lng, radius: 50 }
        : (city ? { city } : {});
      const jobParams = { limit: 50, ...(city ? { city } : {}) };

      const [quickRes, offersRes, openRes, jobsRes] = await Promise.allSettled([
        // 403s for professional-tier verifiers — quick jobs are general-tier only.
        api.get('/simple-verifications/available', { params: quickParams }),
        api.get('/property-checks/offers/mine'),
        api.get('/property-checks/open'),
        api.get('/verifier/marketplace', { params: jobParams })
      ]);

      const merged = [];

      if (quickRes.status === 'fulfilled' && quickRes.value.data?.success) {
        for (const v of quickRes.value.data.data || []) {
          merged.push({
            key: `quick-${v.id}`,
            workType: 'quick',
            id: v.id,
            title: v.title,
            description: v.description,
            fee: parseFloat(v.cost) || 0,
            durationMins: v.aiEstimatedDuration,
            locationText: v.projectLocation || 'Location TBD',
            coords: coordsOf({ lat: v.projectLatitude, lng: v.projectLongitude }),
            distanceKm: v.distanceKm ?? null,
            createdAt: v.createdAt,
            caption: [v.projectName && `Project: ${v.projectName}`,
              (v.clientFirstName || v.clientLastName) && `Client: ${[v.clientFirstName, v.clientLastName].filter(Boolean).join(' ')}`]
              .filter(Boolean)
          });
        }
      }

      if (offersRes.status === 'fulfilled' && offersRes.value.data?.success) {
        for (const o of offersRes.value.data.data?.pendingOffers || []) {
          merged.push({
            key: `pcoffer-${o.offerId}`,
            workType: 'property_check_offer',
            id: o.requestId,
            offerId: o.offerId,
            title: o.package?.displayName || 'Property Check',
            description: (o.scope?.deliverables || []).slice(0, 3).map((d) => d.label || d).join(' · ')
              || 'Visit the property, photograph it, and file the report.',
            fee: o.package?.basePriceUsd ?? 50,
            turnaroundHours: o.package?.turnaroundHours,
            locationText: o.location?.address || o.location?.city || 'See details',
            coords: coordsOf(o.location?.coordinates),
            createdAt: o.offeredAt,
            caption: ['Client has already paid — accept to start.']
          });
        }
      }

      if (openRes.status === 'fulfilled' && openRes.value.data?.success) {
        for (const o of openRes.value.data.data?.openChecks || []) {
          merged.push({
            key: `pcopen-${o.requestId}`,
            workType: 'property_check_open',
            id: o.requestId,
            title: o.package?.displayName || 'Property Check',
            description: (o.scope?.deliverables || []).slice(0, 3).map((d) => d.label || d).join(' · ')
              || 'Visit the property, photograph it, and file the report.',
            fee: o.package?.basePriceUsd ?? 50,
            turnaroundHours: o.package?.turnaroundHours,
            locationText: o.location?.address || o.location?.city || 'See details',
            coords: coordsOf(o.location?.coordinates),
            createdAt: o.createdAt,
            caption: ['Client has already paid — first to claim gets it.']
          });
        }
      }

      if (jobsRes.status === 'fulfilled' && jobsRes.value.data?.success) {
        const payload = jobsRes.value.data.data || {};
        const fixedFee = Number(payload.fixedJobFee) || 75;
        for (const r of payload.requests || []) {
          const details = r.request_details || {};
          merged.push({
            key: `job-${r.id}`,
            workType: 'verification_job',
            id: r.id,
            title: details.title || r.project_title || 'Verification Job',
            description: details.description || '',
            fee: fixedFee,
            locationText: r.location?.address || r.location?.city || r.project_location || 'See details',
            coords: coordsOf(r.location?.coordinates),
            createdAt: r.created_at,
            caption: [r.project_title && `Project: ${r.project_title}`,
              r.milestone_name && `Milestone: ${r.milestone_name}`].filter(Boolean)
          });
        }
      }

      // Distance chips + nearest-first ordering when we know where the verifier is.
      for (const c of merged) {
        if (c.distanceKm == null && gps && c.coords) {
          c.distanceKm = Math.round(haversineKm(gps.lat, gps.lng, c.coords.lat, c.coords.lng) * 10) / 10;
        }
      }
      merged.sort((a, b) => {
        const da = a.distanceKm ?? Infinity;
        const db = b.distanceKm ?? Infinity;
        if (da !== db) return da - db;
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });

      setCards(merged);
    } catch (err) {
      console.error('Error loading available work:', err);
      setError('Failed to load available work. Pull to refresh or try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleManualLocationSearch = () => {
    if (!manualCity.trim()) return;
    loadWork(null, manualCity.trim());
  };

  const removeCard = (key) => setCards((prev) => prev.filter((c) => c.key !== key));

  const handleAcceptError = (err, card) => {
    const data = err?.response?.data;
    if (data?.code === 'MISSING_IDENTITY_DOCUMENT') {
      setBannerKey((k) => k + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (data?.code === 'ALREADY_CLAIMED' || err?.response?.status === 409 || err?.response?.status === 410) {
      setNotice('That job was claimed by another verifier moments ago — it has been removed from your list.');
      removeCard(card.key);
      return;
    }
    alert(data?.error || data?.message || 'Failed to accept this job');
  };

  const handleAccept = async (card) => {
    setAcceptingKey(card.key);
    setNotice(null);
    try {
      if (card.workType === 'quick') {
        const response = await simpleVerificationService.acceptVerification(card.id);
        if (response.success) navigate(`/verifier/verify/${card.id}`);
      } else if (card.workType === 'property_check_offer') {
        await api.post(`/property-checks/offers/${card.offerId}/accept`);
        navigate('/verifier/my-work');
      } else if (card.workType === 'property_check_open') {
        await api.post(`/property-checks/${card.id}/claim`);
        navigate('/verifier/my-work');
      } else if (card.workType === 'verification_job') {
        await api.post(`/verifier/marketplace/requests/${card.id}/accept`);
        navigate('/verifier/my-work');
      }
    } catch (err) {
      console.error('Error accepting work:', err);
      handleAcceptError(err, card);
    } finally {
      setAcceptingKey(null);
    }
  };

  const handleDeclineOffer = async (card) => {
    try {
      await api.post(`/property-checks/offers/${card.offerId}/decline`);
      removeCard(card.key);
    } catch (err) {
      console.error('Error declining offer:', err);
      alert(err?.response?.data?.error || 'Failed to decline offer');
    }
  };

  const renderWorkCard = (card) => {
    const meta = TYPE_META[card.workType];
    const busy = acceptingKey === card.key;
    return (
      <Grid item xs={12} md={6} key={card.key}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Chip
              icon={meta.icon}
              label={meta.label}
              size="small"
              sx={{ bgcolor: `${meta.color}18`, color: meta.color, fontWeight: 600, mb: 1.5, '& .MuiChip-icon': { color: meta.color } }}
            />
            <Typography variant="h6" fontWeight="600" gutterBottom>
              {card.title}
            </Typography>
            {card.description && (
              <Typography variant="body2" color="text.secondary" paragraph>
                {card.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              <Chip icon={<AttachMoney />} label={`$${Number(card.fee).toFixed(2)} fixed fee`} size="small" color="success" />
              {card.durationMins && <Chip icon={<Timer />} label={`~${card.durationMins} mins`} size="small" />}
              {card.turnaroundHours && <Chip icon={<Timer />} label={`Report due in ${card.turnaroundHours}h`} size="small" />}
              <Chip icon={<LocationOn />} label={card.locationText} size="small" />
              {card.distanceKm != null && <Chip label={`${card.distanceKm} km away`} size="small" variant="outlined" />}
            </Box>
            {(card.caption || []).map((line, i) => (
              <Typography key={i} variant="caption" color="text.secondary" display="block">
                {line}
              </Typography>
            ))}
          </CardContent>
          <CardActions sx={{ gap: 1, px: 2, pb: 2 }}>
            <Button
              fullWidth
              variant="contained"
              disabled={busy}
              onClick={() => handleAccept(card)}
              sx={{ background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)' }}
            >
              {busy ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Accept & Start'}
            </Button>
            {card.workType === 'property_check_offer' && (
              <Button variant="outlined" color="inherit" disabled={busy} onClick={() => handleDeclineOffer(card)}>
                Decline
              </Button>
            )}
          </CardActions>
        </Card>
      </Grid>
    );
  };

  const totalEarnings = cards.reduce((sum, c) => sum + (Number(c.fee) || 0), 0);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <VerifierIdentityBanner key={bannerKey} />
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" gutterBottom>
          Find Work
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Every job here is a fixed fee — first verifier to accept gets it. No quoting, no waiting.
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
          <Tab label="Available Work" />
          <Tab label="My Responses" />
        </Tabs>
      </Paper>

      {activeTab === 1 && <VerifierMarketplacePage embedded fixedTab={2} />}

      {activeTab === 0 && (
        <>
          {/* Profile completeness banner (V10) */}
          {!profileComplete && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              action={
                <Button color="inherit" size="small" onClick={() => navigate('/verifier/profile')}>
                  Complete Profile
                </Button>
              }
            >
              Complete your profile to accept verification jobs — add your qualifications and location.
            </Alert>
          )}

          {/* GPS denied — manual location fallback */}
          {gpsDenied && !gpsLocation && (
            <Paper sx={{ p: 3, mb: 3, border: `1px solid ${brand.accent || '#059669'}30`, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MyLocation sx={{ color: brand.accent || '#059669' }} />
                <Typography variant="subtitle1" fontWeight="600">
                  Location access unavailable
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We couldn't access your GPS location, so jobs aren't sorted by distance. Enter your city to focus the list, or enable location access and refresh.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  placeholder="e.g. Harare, Bulawayo, Mutare..."
                  value={manualCity}
                  onChange={(e) => setManualCity(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualLocationSearch()}
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ fontSize: 20, color: 'text.secondary' }} />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleManualLocationSearch}
                  disabled={!manualCity.trim() || loading}
                  startIcon={loading ? <CircularProgress size={16} /> : <Search />}
                  sx={{ bgcolor: brand.accent || '#059669', '&:hover': { bgcolor: '#047857' }, whiteSpace: 'nowrap' }}
                >
                  Find Work
                </Button>
              </Box>
              <Button
                size="small"
                onClick={captureGPS}
                sx={{ mt: 1, color: brand.accent || '#059669', textTransform: 'none' }}
                startIcon={<MyLocation sx={{ fontSize: 16 }} />}
              >
                Try GPS again
              </Button>
            </Paper>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {notice && (
            <Alert severity="info" sx={{ mb: 3 }} onClose={() => setNotice(null)}>
              {notice}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Stats */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary" fontWeight="700">
                      {cards.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Jobs Available Now
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" fontWeight="700">
                      ${totalEarnings.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Earnings Potential
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Unified work list */}
              {cards.length === 0 ? (
                <Paper sx={{ p: 5, textAlign: 'center' }}>
                  <VerifiedUser sx={{ fontSize: 64, color: brand.accent || '#43B581', mb: 2, opacity: 0.6 }} />
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    No Work Available Right Now
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
                    Jobs appear here the moment property owners book Property Checks or request project verifications.
                  </Typography>
                  <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto', mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>Make sure your profile lists your location — we match you to nearby jobs</li>
                      <li>New jobs are posted regularly — check back soon</li>
                      <li>Every job pays a fixed fee, first to accept gets it</li>
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => navigate(profileComplete ? '/dashboard' : '/verifier/profile')}
                    sx={{ bgcolor: brand.accent || '#43B581', '&:hover': { bgcolor: '#059669' } }}
                  >
                    {profileComplete ? 'Back to Dashboard' : 'Complete Your Profile'}
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {cards.map(renderWorkCard)}
                </Grid>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default AvailableSimpleVerificationsPage;
